/**
 * Status Change Approval Component
 *
 * Inline approval UI for status change tool calls.
 */

import React from 'react';
import { RefreshCw, Check, X } from 'lucide-react';
import type { ToolCall } from '../../hooks/useWorkflowChat';

interface StatusChangeApprovalProps {
  toolCall: ToolCall;
  nodeName?: string;
  onApprove: () => void;
  onReject: () => void;
}

// Status display colors
const statusColors: Record<string, string> = {
  pending: 'text-text-muted',
  ready: 'text-primary-400',
  running: 'text-primary-400',
  attention: 'text-warning-400',
  completed: 'text-success-400',
  sleeping: 'text-text-muted',
  failed: 'text-red-400',
};

export function StatusChangeApproval({
  toolCall,
  nodeName,
  onApprove,
  onReject,
}: StatusChangeApprovalProps) {
  const { node_id, status } = toolCall.input as { node_id: string; status: string };
  const displayName = nodeName || node_id.slice(0, 8);
  const statusColor = statusColors[status] || 'text-text-primary';

  return (
    <div className="bg-surface-800 border border-warning-500/30 rounded-lg p-3 my-2">
      <div className="flex items-center gap-2 mb-3">
        <RefreshCw className="w-4 h-4 text-warning-400" />
        <span className="text-sm text-text-primary">
          Change status: <span className="font-medium">"{displayName}"</span> →{' '}
          <span className={`font-medium ${statusColor}`}>{status}</span>
        </span>
      </div>
      <div className="flex items-center gap-2">
        <button
          onClick={onApprove}
          className="flex items-center gap-1.5 px-3 py-1.5 bg-success-500/20 hover:bg-success-500/30 text-success-400 rounded text-sm font-medium transition-colors"
        >
          <Check className="w-3.5 h-3.5" />
          Approve
        </button>
        <button
          onClick={onReject}
          className="flex items-center gap-1.5 px-3 py-1.5 bg-surface-700 hover:bg-surface-600 text-text-muted rounded text-sm font-medium transition-colors"
        >
          <X className="w-3.5 h-3.5" />
          Cancel
        </button>
      </div>
    </div>
  );
}
